import { SxProps } from '@mui/material';
import { CSSProperties } from 'react';
import { themeObject, validationsMessages } from '../../lib';
import { AttributeAssociationItem } from '../../pages/edge-definitions/edge-definition-helper';
import { AttributeAssociationWidgetProps } from '../../pages/edge-definitions/edge-definition-manager/create-edge-definition/create-edge-definition-helper';

export type PartialAttributeAssociationWidgetProps = Omit<
    AttributeAssociationWidgetProps,
    'leftSideSelectionOptions' | 'rightSideSelectionOptions'
>;

export interface PairedInputSelectionWidgetProps
    extends PartialAttributeAssociationWidgetProps {
    title: string;
    addButtonLabel: string;
    duplicateErrorMessage: string;
    emptyErrorMessage: string;
    leftSideInputLabel: string;
    rightSideInputLabel: string;
    checkForDuplicates: boolean;
    widgetMainContainerStyles: CSSProperties;
    titleContainerStyles: CSSProperties;
    leftSideInputStyles: CSSProperties;
    rightSideInputStyles: CSSProperties;
    customInputSelectionStyles?: CSSProperties | SxProps;
    addButtonContainerStyles: CSSProperties;
    addButtonStyles: CSSProperties;
    displayEmptyState?: boolean;
    emptyStateIcon?: string;
    emptyStateIconStyles?: CSSProperties;
    emptyStateHeadingText?: string;
    emptyStateBodyText?: string;
    emptyStateMainContainerStyles?: SxProps;
    emptyStateItemContainerStyles?: SxProps;
    emptyStateHeadingTextStyles?: SxProps;
    emptyStateBodyTextStyles?: SxProps;
    emptyStateHeadingTextContainerStyles?: SxProps;
    emptyStateBodyTextContainerStyles?: SxProps;
    customHeadingContainerStyles?: SxProps;
    customHeadingTextStyles?: SxProps;
    deleteButtonContainerStyles?: SxProps;
    deleteButtonStyles?: SxProps;
}

export interface PairedInputSelectionProps {
    leftSideInputLabel: string;
    rightSideInputLabel: string;
    leftSideInputStyles: CSSProperties;
    rightSideInputStyles: CSSProperties;
    deleteButtonContainerStyles?: SxProps;
    deleteButtonStyles?: SxProps;
    customInputSelectionStyles?: CSSProperties | SxProps;
    emptyErrorMessage: string;
    handleRemove: () => void;
    attributeAssociationItem: AttributeAssociationItem;
    onAttributeAssociationChanged: (
        associationItem: AttributeAssociationItem
    ) => void;
}

export const pairedInputSelectionWidgetStyles = {
    mainContainer: {
        width: '100%',
    },
    titleContainer: {
        width: '100%',
        mt: 3,
    },
    bodyContainer: {
        width: '100%',
    },
    attributeAssociationTitle: {
        ...themeObject.typography.h4,
        color: themeObject.palette.primary[700],
    },
    listContainer: {
        width: '100%',
        py: 0,
    },
    addButtonContainer: {
        width: '100%',
        pt: 1,
    },
    addButton: {
        borderRadius: '20px',
        width: '249px',
        height: '40.25px',
        '& .MuiTypography-root': {
            ...themeObject.components.MuiButton.styleOverrides.root,
            ...themeObject.components.MuiButton.styleOverrides.medium,
            color: themeObject.palette.grayScale[700],
            ml: '3px',
        },
    },
};

export const pairedInputSelectionStyles = {
    attributeAssociationSelectionContainer: {
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        mt: 3,
    },
    associationLeftSideContainer: {
        width: '100%',
    },
    associationRightSideContainer: {
        width: '100%',
        ml: 1.5,
    },
    deleteAssociationButtonContainer: {
        width: '100%',
        height: '100%',
        mt: 3.5,
        ml: 1.5,
    },
    deleteButton: {
        width: '20px',
        height: '20px',
        mt: 1,
        backgroundColor: themeObject.palette.grayScale[500],
        color: themeObject.palette.background.default,
    },
};

export const isValueNonEmpty = (value: string): boolean => {
    return !!value && value.trim() !== '';
};

export const isAttributeAssociationNonEmpty = (
    list: AttributeAssociationItem[]
): boolean => {
    return list.every(
        (item) =>
            !item.leftSideError &&
            !item.rightSideError &&
            isValueNonEmpty(item.attributeAssociation.leftSideValue) &&
            isValueNonEmpty(item.attributeAssociation.rightSideValue)
    );
};

export const checkForInvalidProperties = (
    list: AttributeAssociationItem[]
): {
    isListValid: boolean;
    updatedList: AttributeAssociationItem[];
} => {
    let isListInvalid = false;

    // check for any item that have any error already in the list. IF the item is found the list is considered as invalid.
    const itemWithError: AttributeAssociationItem | undefined = list.find(
        (item) => item.leftSideError || item.rightSideError
    );
    if (itemWithError) {
        return {
            isListValid: isListInvalid,
            updatedList: list,
        };
    }

    // If the list doesn't have any errors from the check above, check for any empty values. If an empty value is found, the list is invalid.
    const updatedList = list.map((association) => {
        const isEmptyLeftSideValue =
            !association.attributeAssociation.leftSideValue;
        const isEmptyRightSideValue =
            !association.attributeAssociation.rightSideValue;

        if (isEmptyLeftSideValue || isEmptyRightSideValue) {
            isListInvalid = true;
        }

        return {
            ...association,
            leftSideError: isEmptyLeftSideValue,
            leftSideErrorMessage: isEmptyLeftSideValue
                ? validationsMessages.required
                : '',
            rightSideError: isEmptyRightSideValue,
            rightSideErrorMessage: isEmptyRightSideValue
                ? validationsMessages.required
                : '',
        };
    });

    return {
        isListValid: !isListInvalid,
        updatedList: updatedList,
    };
};

export const possiblyUpdateEmptyParameterInputErrors = (
    list: AttributeAssociationItem[],
    emptyErrorMessage: string
): AttributeAssociationItem[] => {
    return list.map((item) => {
        const leftSideValueValid: boolean = isValueNonEmpty(
            item.attributeAssociation.leftSideValue
        );
        const rightSideValueValid: boolean = isValueNonEmpty(
            item.attributeAssociation.rightSideValue
        );

        return {
            ...item,
            leftSideError: !leftSideValueValid,
            leftSideErrorMessage: leftSideValueValid ? '' : emptyErrorMessage,
            rightSideError: !rightSideValueValid,
            rightSideErrorMessage: rightSideValueValid ? '' : emptyErrorMessage,
        };
    });
};

export const updateEmptyParameterInputErrors = (
    list: AttributeAssociationItem[],
    onChange: (list: AttributeAssociationItem[]) => void,
    emptyErrorMessage: string
): void => {
    onChange(possiblyUpdateEmptyParameterInputErrors(list, emptyErrorMessage));
};
